import styled, { createGlobalStyle, keyframes } from "styled-components";

const playerEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const loadingPulse = keyframes`
  0%, 100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
`;

export const GamePlayerGlobalStyle = createGlobalStyle`
  @media (max-width: 1023px) {
    html.crypto-game-player-route,
    body.crypto-game-player-route {
      width: 100%;
      min-height: 100dvh;
      overflow: hidden;
      overscroll-behavior: none;
      background: #000;
    }

    body.crypto-game-player-route .top-nav,
    body.crypto-game-player-route .buttons-container,
    body.crypto-game-player-route .mobile-sidebar-backdrop,
    body.crypto-game-player-route .sidebar-open-panel,
    body.crypto-game-player-route .sidebar-closed-panel {
      display: none !important;
    }
  }
`;

export const StyledGamePlayerPage = styled.div`
  width: 100%;
  min-height: calc(100vh - 128px);
  color: #f4f8fb;

  .game-player-shell {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-top: -10px;
    animation: ${playerEnter} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }

  .game-player-controlbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    min-height: 62px;
    padding: 10px 12px 10px 14px;
    border-radius: 8px;
    border: 1px solid rgba(139, 177, 205, 0.18);
    background: rgba(6, 18, 25, 0.76);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.18);
  }

  .game-player-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .game-player-title h1 {
    margin: 0;
    color: #ffffff;
    font-size: 17px;
    line-height: 1.2;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .game-player-title span {
    color: #91a8b8;
    font-size: 12px;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .game-player-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .game-player-action {
    height: 36px;
    border: 0;
    border-radius: 8px;
    padding: 0 12px;
    color: #d9e7ef;
    background: rgba(35, 52, 64, 0.9);
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
    transition:
      transform 180ms ease,
      background 180ms ease,
      color 180ms ease;
  }

  .game-player-action:hover,
  .game-player-action:focus-visible {
    transform: translateY(-1px);
    background: rgba(65, 86, 101, 0.9);
    color: #ffffff;
  }

  .game-player-action.mode {
    border: 1px solid rgba(139, 177, 205, 0.16);
    background: rgba(17, 34, 45, 0.9);
  }

  .game-player-action.mode.is-active {
    color: var(--crypto-accent);
    border-color: rgba(89, 244, 119, 0.82);
    background:
      radial-gradient(circle at 50% 0%, rgba(89, 244, 119, 0.22), rgba(8, 24, 32, 0.92) 68%),
      rgba(8, 24, 32, 0.95);
    box-shadow:
      0 0 0 1px rgba(89, 244, 119, 0.18),
      0 12px 28px rgba(89, 244, 119, 0.18),
      inset 0 0 24px rgba(89, 244, 119, 0.09);
    text-shadow: 0 0 12px rgba(89, 244, 119, 0.52);
  }

  .game-player-action.primary {
    color: #07131b;
    background: var(--crypto-accent);
    box-shadow: 0 12px 26px rgba(89, 244, 119, 0.16);
  }

  .game-player-stage {
    position: relative;
    width: 100%;
    min-height: clamp(520px, calc(100vh - 168px), 780px);
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid rgba(139, 177, 205, 0.18);
    background: #000;
    box-shadow: 0 22px 56px rgba(0, 0, 0, 0.34);
  }

  .game-player-shell.is-theater {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .game-player-shell.is-theater .game-player-stage {
    height: calc(100dvh - 164px);
    min-height: clamp(680px, calc(100dvh - 164px), 980px);
  }

  .game-player-frame {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
    background: #000;
  }

  .game-player-loading {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: grid;
    place-items: center;
    pointer-events: none;
    background:
      radial-gradient(circle at center, rgba(89, 244, 119, 0.12), transparent 28%),
      #000;
    transition: opacity 220ms ease, visibility 220ms ease;
  }

  .game-player-loading.is-hidden {
    opacity: 0;
    visibility: hidden;
  }

  .game-player-loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #c9d7df;
    font-size: 13px;
    font-weight: 800;
    animation: ${loadingPulse} 1.3s ease-in-out infinite;
  }

  .game-player-loading-mark {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    border: 4px solid rgba(89, 244, 119, 0.18);
    border-top-color: var(--crypto-accent);
    animation: spin 900ms linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .game-player-empty {
    display: grid;
    place-items: center;
    min-height: 420px;
    border-radius: 8px;
    border: 1px solid rgba(139, 177, 205, 0.18);
    background: rgba(7, 19, 27, 0.7);
    text-align: center;
    padding: 36px 18px;
  }

  .game-player-empty h1 {
    margin: 0 0 10px;
    color: #ffffff;
    font-size: 24px;
    font-weight: 900;
  }

  .game-player-empty p {
    margin: 0 0 18px;
    color: #9fb2c1;
    font-size: 14px;
    font-weight: 600;
  }

  .game-player-mobile-exit {
    display: none;
  }

  @media (max-width: 1023px) {
    position: fixed;
    inset: 0;
    z-index: 160;
    min-height: 100dvh;
    padding: 0;
    background: #000;
    padding-bottom: 0;

    .game-player-shell {
      gap: 0;
      min-height: 100dvh;
      margin-top: 0;
    }

    .game-player-stage {
      height: calc(100dvh - 54px - env(safe-area-inset-bottom));
      min-height: calc(100dvh - 54px - env(safe-area-inset-bottom));
      border: 0;
      border-radius: 0;
      box-shadow: none;
    }

    .game-player-shell.is-theater .game-player-stage {
      height: calc(100dvh - 54px - env(safe-area-inset-bottom));
      min-height: calc(100dvh - 54px - env(safe-area-inset-bottom));
    }

    .game-player-controlbar {
      display: none;
    }

    .game-player-mobile-exit {
      position: fixed;
      z-index: 170;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      height: calc(54px + env(safe-area-inset-bottom));
      padding: 0 14px env(safe-area-inset-bottom);
      border: 0;
      border-top: 1px solid rgba(139, 177, 205, 0.22);
      color: #ffffff;
      background: linear-gradient(180deg, rgba(12, 29, 38, 0.98), rgba(7, 19, 27, 0.98));
      font-size: 15px;
      font-weight: 900;
      cursor: pointer;
      box-shadow: 0 -12px 28px rgba(0, 0, 0, 0.35);
    }
  }

  @media (max-width: 575px) {
    .game-player-stage {
      height: calc(100dvh - 52px - env(safe-area-inset-bottom));
      min-height: calc(100dvh - 52px - env(safe-area-inset-bottom));
    }
  }
`;
